import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  Activity, ArrowDownRight, ArrowRight, ArrowUpRight, MapPin, RefreshCw,
  Sparkles, UserCheck, Users,
} from 'lucide-react';
import { UserStats, fetchUserStats } from '../services/api';

/**
 * Series colours.
 *
 * The house palette (brand green against clay) fails as a categorical pair -
 * both read as grey, and they sit 13.4 apart for normal vision and 4.7 under
 * protanopia, well under the usable floor. Signups and purchases are drawn
 * side by side and have to be told apart, so they use a blue/orange axis
 * instead, which clears CVD separation comfortably. The growth chart is a
 * single series with nothing to be confused with, so it keeps brand green.
 */
const SERIES_SIGNUPS = '#0369A1';
const SERIES_PRO = '#C2410C';

const GRID = 'rgb(var(--ink-200))';
const AXIS_TEXT = 'rgb(var(--ink-400))';

const MicroLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-ink-400">
    {children}
  </span>
);

/** Month key ("2026-09") to "Sep". Parsed as UTC so the month never slips. */
const shortMonth = (key: string) => {
  const [y, m] = key.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-GB', {
    month: 'short', timeZone: 'UTC',
  });
};

const longMonth = (key: string) => {
  const [y, m] = key.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-GB', {
    month: 'long', year: 'numeric', timeZone: 'UTC',
  });
};

const tooltipStyle = {
  background: 'rgb(var(--surface))',
  border: '1px solid rgb(var(--ink-200))',
  borderRadius: 10,
  fontSize: 12,
};

interface MetricProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  detail?: React.ReactNode;
  accent?: string;
}

const Metric: React.FC<MetricProps> = ({ icon, label, value, detail, accent }) => (
  <div className="bg-surface rounded-xl shadow-sm p-5">
    <div className="flex items-start gap-4">
      <div className={`p-2.5 rounded-lg flex-shrink-0 ${accent || 'text-brand-600 bg-brand-50'}`}>
        {icon}
      </div>
      <div className="min-w-0">
        <MicroLabel>{label}</MicroLabel>
        <p className="text-2xl font-semibold text-ink-900 mt-1.5 tabular-nums leading-none">
          {typeof value === 'number' ? value.toLocaleString() : value}
        </p>
        {detail && <p className="text-xs text-ink-500 mt-2 leading-relaxed">{detail}</p>}
      </div>
    </div>
  </div>
);

const ChartCard: React.FC<{
  title: string;
  caption: string;
  children: React.ReactElement;
}> = ({ title, caption, children }) => (
  <div className="bg-surface rounded-xl shadow-sm p-5">
    <div className="mb-4">
      <h2 className="font-display font-normal tracking-tight text-lg text-ink-900">{title}</h2>
      <p className="text-xs text-ink-400 mt-0.5">{caption}</p>
    </div>
    <div className="h-[260px]">
      <ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer>
    </div>
  </div>
);

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<UserStats | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const loadStats = useCallback(async () => {
    setIsRefreshing(true);
    try {
      setStats(await fetchUserStats());
      setError(false);
    } catch (err) {
      setError(true);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
    const interval = setInterval(loadStats, 300000);
    return () => clearInterval(interval);
  }, [loadStats]);

  const months = useMemo(() => stats?.monthly ?? [], [stats]);

  /**
   * Month-on-month change in signups.
   *
   * Deliberately compares the last two *complete* months. The final entry is
   * the month in progress, so including it compared a part-month against a
   * whole one and reported a fall on almost every day of every month.
   */
  const trend = useMemo(() => {
    if (months.length < 3) return null;
    const [prev, last] = months.slice(-3, -1);
    if (prev.signups === 0) return null;
    return Math.round(((last.signups - prev.signups) / prev.signups) * 100);
  }, [months]);

  const completeMonth = months.length >= 2 ? months[months.length - 2] : null;

  // Running total, seeded with everyone who joined before the window - without
  // the baseline the growth line would restart from zero every month.
  const growth = useMemo(() => {
    let running = stats?.baseline_users ?? 0;
    return months.map((m) => {
      running += m.signups;
      return { month: m.month, total: running };
    });
  }, [months, stats]);

  const conversion = stats && stats.total_users
    ? Math.round((stats.pro_purchases / stats.total_users) * 1000) / 10
    : 0;

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-500" />
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-3 text-center">
        <p className="text-sm text-ink-600">Could not load the analytics.</p>
        <button
          type="button"
          onClick={loadStats}
          className="px-5 py-2.5 rounded-lg text-sm font-medium text-brand-600 bg-brand-50 hover:bg-brand-100"
        >
          Try again
        </button>
      </div>
    );
  }

  const currentMonth = months[months.length - 1];

  return (
    <div className="container mx-auto max-w-[1400px]">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5 mb-8">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-3.5 h-3.5 text-brand-500" />
            <MicroLabel>Admin - product analytics</MicroLabel>
          </div>
          <h1 className="font-display font-normal tracking-tight text-3xl text-ink-900">
            Analytics
          </h1>
          <p className="text-sm text-ink-500 mt-2 font-light">
            Signups, PRO conversion and how much the guide is actually used.
          </p>
        </div>

        {/* Wraps: unwrapped these are wider than a phone. */}
        <div className="flex flex-wrap items-center gap-3">
          <Link
            to="/dashboard/scrapers"
            className="flex items-center px-5 py-2.5 rounded-lg text-sm font-medium text-ink-600 bg-ink-50 hover:bg-ink-100 transition-colors duration-200"
          >
            <Activity className="w-4 h-4 mr-2" />
            Scraper status
          </Link>
          <button
            type="button"
            onClick={loadStats}
            disabled={isRefreshing}
            className={`flex items-center px-5 py-2.5 rounded-lg text-sm font-medium text-brand-600 bg-brand-50 hover:bg-brand-100 transition-colors duration-200 ${
              isRefreshing ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 mb-6">
        <Metric
          icon={<Users className="w-5 h-5" />}
          label="Total users"
          value={stats.total_users}
          detail={`${stats.new_users_30d.toLocaleString()} joined in the last 30 days`}
        />
        <Metric
          icon={<Sparkles className="w-5 h-5" />}
          label="PRO purchases"
          value={stats.pro_purchases}
          accent="text-orange-700 bg-orange-50"
          detail={`${conversion}% of users have bought - ${stats.pro_active.toLocaleString()} active right now`}
        />
        <Metric
          icon={<UserCheck className="w-5 h-5" />}
          label="Active users"
          value={stats.active_users_30d}
          accent="text-emerald-600 bg-emerald-50"
          detail="Signed in within the last 30 days"
        />
        <Metric
          icon={<MapPin className="w-5 h-5" />}
          label="Venues marked visited"
          value={stats.total_visits}
          accent="text-sky-700 bg-sky-50"
          detail={`${stats.visits_30d.toLocaleString()} in the last 30 days`}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <ChartCard
          title="Signups and PRO purchases"
          caption={
            currentMonth
              ? `Last ${months.length} months - ${shortMonth(currentMonth.month)} is month to date`
              : `Last ${months.length} months`
          }
        >
          <BarChart data={months} margin={{ top: 8, right: 8, left: -18, bottom: 0 }} barGap={2}>
            <CartesianGrid stroke={GRID} strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="month" tickFormatter={shortMonth} tickLine={false} axisLine={false}
              tick={{ fill: AXIS_TEXT, fontSize: 11 }}
            />
            <YAxis
              allowDecimals={false} tickLine={false} axisLine={false}
              tick={{ fill: AXIS_TEXT, fontSize: 11 }}
            />
            <Tooltip
              cursor={{ fill: 'rgb(var(--ink-100) / 0.6)' }}
              contentStyle={tooltipStyle}
              labelFormatter={longMonth}
            />
            <Legend
              iconType="circle" iconSize={8}
              wrapperStyle={{ fontSize: 12, color: AXIS_TEXT, paddingTop: 8 }}
            />
            <Bar dataKey="signups" name="Signups" fill={SERIES_SIGNUPS} radius={[4, 4, 0, 0]} maxBarSize={26} />
            <Bar dataKey="pro" name="PRO purchases" fill={SERIES_PRO} radius={[4, 4, 0, 0]} maxBarSize={26} />
          </BarChart>
        </ChartCard>

        <ChartCard
          title="Total users"
          caption={
            trend === null || !completeMonth
              ? 'Running total of all registered accounts'
              : `Running total - signups ${trend >= 0 ? 'up' : 'down'} ${Math.abs(trend)}% in ${shortMonth(completeMonth.month)}`
          }
        >
          <AreaChart data={growth} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
            <CartesianGrid stroke={GRID} strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="month" tickFormatter={shortMonth} tickLine={false} axisLine={false}
              tick={{ fill: AXIS_TEXT, fontSize: 11 }}
            />
            <YAxis
              allowDecimals={false} tickLine={false} axisLine={false}
              tick={{ fill: AXIS_TEXT, fontSize: 11 }}
            />
            <Tooltip
              contentStyle={tooltipStyle}
              labelFormatter={longMonth}
              formatter={(value: number) => [value.toLocaleString(), 'Total users']}
            />
            <Area
              type="monotone" dataKey="total" strokeWidth={2}
              stroke="rgb(var(--brand-500))" fill="rgb(var(--brand-500) / 0.14)"
            />
          </AreaChart>
        </ChartCard>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mt-6">
        <p className="text-[11px] text-ink-400">
          {trend !== null && completeMonth && (
            <span className={`inline-flex items-center gap-1 mr-2 ${trend >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
              {trend >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
              {trend >= 0 ? '+' : ''}{trend}%
            </span>
          )}
          Month-on-month compares the last two complete months, so the month in progress never drags it down.
        </p>
        <Link
          to="/dashboard/scrapers"
          className="inline-flex items-center gap-1.5 text-[11px] text-ink-500 hover:text-ink-700"
        >
          Scraper health <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
};

export default AdminDashboard;
